    /**
     * Infinite Scroll for Search Results
     * Automatically loads next page when:
     * - User scrolls near bottom
     * - Fewer than minimum products are visible (handles location filtering)
     */

    class SearchInfiniteScroll {
    constructor() {
        // Singleton guard. This script self-initializes on DOMContentLoaded AND the inline
        // script in main-search.liquid calls `new SearchInfiniteScroll()`. Without this guard
        // two instances run, each with its own loadedUrls set, so both fetch and append the
        // same next page -> duplicate product cards while scrolling.
        if (window.searchInfiniteScroll) {
        return window.searchInfiniteScroll;
        }

        this.productGrid = document.querySelector('#product-grid .product-grid');
        this.pagination = document.querySelector('.pagination-wrapper');
        this.loadingIndicator = document.querySelector('.infinite-scroll-loading');
        this.isLoading = false;
        this.hasMorePages = true;
        this.minVisibleProducts = 12; // Trigger auto-load if fewer than this many products
        this.loadedUrls = new Set(); // Track loaded URLs to prevent duplicates
        // Bumped on every reinit (filter/sort change). A loadNextPage() call captures the
        // generation before fetching and discards its result if the generation changed while
        // the request was in flight, so a pre-filter page load can't pollute the filtered grid.
        this.loadGeneration = 0;
        
        if (!this.productGrid) {
        console.log('Infinite scroll: No product grid found');
        return;
        }
        
        if (!this.loadingIndicator) {
        console.log('Infinite scroll: No loading indicator found');
        return;
        }
        
        window.searchInfiniteScroll = this;
        this.init();
    }
    
    init() {
        console.log('Search infinite scroll initialized');
        
        // Track the current page URL to prevent reloading it
        this.loadedUrls.add(this.withLocationFilter(window.location.href));
        
        // Check if pagination exists
        this.checkPagination();
        
        // Set up scroll listener
        this.setupScrollListener();
        
        // Set up mutation observer to watch for product removal/addition
        this.setupProductObserver();
        
        // Watch for facets.js replacing the whole grid (filter/sort/remove actions) so we
        // can re-capture the new grid + pagination nodes and keep paginating the filtered set.
        this.setupGridReplacementObserver();
        
        // Set up event listeners
        this.setupEventListeners();
        
        // Check immediately if we need to load more
        setTimeout(() => this.checkIfNeedsMoreProducts(), 1000);
        
        // NOTE: Filter count recalculation is DISABLED for native S&D filters
        // Shopify provides accurate counts from the backend, no client-side calculation needed
        // setTimeout(() => this.recalculateFilterCounts(), 1500);
    }
    
    setupEventListeners() {
        // NOTE: Filter count recalculation is DISABLED for native S&D filters
        // Listen for location changes to recalculate filter counts
        // document.addEventListener('locationChanged', () => {
        //     console.log('Location changed, recalculating filter counts...');
        //     setTimeout(() => {
        //         this.recalculateFilterCounts();
        //     }, 500);
        // });
    }
    
    setupScrollListener() {
        let scrollTimeout;
        window.addEventListener('scroll', () => {
        if (scrollTimeout) clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
            this.checkScrollPosition();
        }, 150);
        });
    }
    
    setupProductObserver() {
        // Re-observing after a re-init: drop the old observer (its target node was replaced).
        if (this.productObserver) this.productObserver.disconnect();

        // Watch for products being removed by location filtering
        this.productObserver = new MutationObserver((mutations) => {
        // Debounce the check
        clearTimeout(this.productCheckTimeout);
        this.productCheckTimeout = setTimeout(() => {
            this.checkIfNeedsMoreProducts();
        }, 500);
        });
        
        this.productObserver.observe(this.productGrid, {
        childList: true,
        subtree: false
        });
    }

    setupGridReplacementObserver() {
        // #ProductGridContainer is a stable node; facets.js swaps its innerHTML on every
        // filter/sort/remove. Our own infinite-scroll appends mutate descendants (the inner
        // .product-grid <ul> and .pagination-wrapper), NOT the container's direct children,
        // so with subtree:false this observer fires ONLY on facet-driven grid replacement.
        const container = document.getElementById('ProductGridContainer');
        if (!container) return;

        this.gridReplacementObserver = new MutationObserver(() => {
        this.reinit();
        });
        this.gridReplacementObserver.observe(container, {
        childList: true,
        subtree: false
        });
    }

    reinit() {
        // Debounce: facets.js can mutate the container more than once per update.
        clearTimeout(this.reinitTimeout);
        this.reinitTimeout = setTimeout(() => {
        const newGrid = document.querySelector('#product-grid .product-grid');
        if (!newGrid) {
            // Empty/filtered-to-nothing result set: nothing more to paginate.
            this.hasMorePages = false;
            return;
        }

        this.productGrid = newGrid;
        this.pagination = document.querySelector('.pagination-wrapper');
        this.loadingIndicator =
            document.querySelector('.infinite-scroll-loading') || this.loadingIndicator;
        this.isLoading = false;
        this.hasMorePages = true;
        // Invalidate any page load that is still in flight from the previous result set.
        this.loadGeneration++;
        // Reset dedupe tracking to the now-current (filtered) URL.
        this.loadedUrls = new Set([this.withLocationFilter(window.location.href)]);

        this.checkPagination();
        this.setupProductObserver();
        console.log('Search infinite scroll re-initialized after filter/sort change');

        setTimeout(() => this.checkIfNeedsMoreProducts(), 500);
        }, 300);
    }
    
    checkPagination() {
        if (!this.pagination) {
        console.log('No pagination found - single page');
        this.hasMorePages = false;
        return;
        }
        
        // The "Next" link uses class pagination__item--prev (confusing naming in Shopify)
        const nextLink = this.pagination.querySelector('.pagination__item--prev');
        if (!nextLink) {
        console.log('No next page available');
        this.hasMorePages = false;
        } else {
        console.log('Next page found, infinite scroll ready');
        }
    }
    
    checkScrollPosition() {
        if (!this.hasMorePages || this.isLoading) return;
        
        // Check if the loading indicator (which is positioned at the pagination location) is in view
        const loadingRect = this.loadingIndicator.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        
        // Trigger when loading indicator is within 400px of viewport
        if (loadingRect.top < windowHeight + 400) {
        console.log('Near loading indicator/pagination, loading next page...');
        this.loadNextPage();
        }
    }
    
    checkIfNeedsMoreProducts() {
        if (!this.hasMorePages || this.isLoading) return;
        
        // Count visible products (excluding loading state)
        const visibleProducts = this.productGrid.querySelectorAll('.product-card:not(.product-loading)').length;
        
        console.log(`Visible products: ${visibleProducts}, Minimum: ${this.minVisibleProducts}`);
        
        if (visibleProducts < this.minVisibleProducts) {
        console.log('Too few products visible, auto-loading next page...');
        this.loadNextPage();
        }
    }
    
    async loadNextPage() {
        if (this.isLoading || !this.hasMorePages) return;
        
        this.isLoading = true;
        this.showLoadingIndicator();
        const requestGeneration = this.loadGeneration;
        
        try {
        // Get next page URL (pagination__item--prev is the "Next" button in Shopify's pagination)
        const nextLink = this.pagination.querySelector('.pagination__item--prev');
        if (!nextLink) {
            this.hasMorePages = false;
            this.hideLoadingIndicator();
            this.isLoading = false;
            return;
        }
        
        const nextUrl = this.withLocationFilter(nextLink.href);
        
        // Check if we've already loaded this URL (prevents duplicate page loads)
        if (this.loadedUrls.has(nextUrl)) {
            console.log('Already loaded this page, stopping:', nextUrl);
            this.hasMorePages = false;
            this.hideLoadingIndicator();
            this.isLoading = false;
            return;
        }
        
        console.log('Loading next page:', nextUrl);
        this.loadedUrls.add(nextUrl);
        
        // Fetch next page
        const response = await fetch(nextUrl);
        const html = await response.text();
        
        // A filter/sort happened while this request was in flight: its results belong to the
        // old (pre-filter) result set, so discard them rather than append into the new grid.
        if (requestGeneration !== this.loadGeneration) {
            console.log('Discarding stale page load (filter/sort changed mid-request):', nextUrl);
            this.isLoading = false;
            this.hideLoadingIndicator();
            return;
        }
        
        // Parse response
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        // Get products from next page
        const nextProducts = doc.querySelectorAll('#product-grid .product-grid .product-card');
        console.log(`Found ${nextProducts.length} products on next page`);
        console.log('Current product grid:', this.productGrid);
        console.log('Current products in grid:', this.productGrid.querySelectorAll('.product-card').length);
        
        if (nextProducts.length === 0) {
            console.log('No products found in next page HTML');
            this.hasMorePages = false;
            this.hideLoadingIndicator();
            this.isLoading = false;
            return;
        }
        
        // Append products to current grid
        const newCards = [];
        
        nextProducts.forEach(product => {
            const clonedProduct = product.cloneNode(true);
            
            // Fix HTML-encoded JSON attributes
            const locationPrices = clonedProduct.getAttribute('data-location-prices');
            const specialLocations = clonedProduct.getAttribute('data-special-locations');
            const locationSpecialTexts = clonedProduct.getAttribute('data-location-special-texts');
            
            if (locationPrices) {
            // Decode HTML entities
            const decodedPrices = this.decodeHTMLEntities(locationPrices);
            clonedProduct.setAttribute('data-location-prices', decodedPrices);
            }
            
            if (specialLocations) {
            const decodedSpecialLocations = this.decodeHTMLEntities(specialLocations);
            clonedProduct.setAttribute('data-special-locations', decodedSpecialLocations);
            }
            
            if (locationSpecialTexts) {
            const decodedTexts = this.decodeHTMLEntities(locationSpecialTexts);
            clonedProduct.setAttribute('data-location-special-texts', decodedTexts);
            }
            
            // Fix missing images
            const img = clonedProduct.querySelector('.card__media img');
            if (img && (!img.src || img.src === '' || img.src === window.location.href)) {
            const placeholderUrl = 'https://cdn.shopify.com/s/files/1/0644/3935/3524/files/placeholder-image.webp?v=1749605266';
            img.src = placeholderUrl;
            img.alt = clonedProduct.getAttribute('data-title') || 'Product image';
            img.width = '200';
            img.height = '200';
            }
            
            // Remove processed flag so it gets re-processed
            clonedProduct.removeAttribute('data-processed');
            
            this.productGrid.appendChild(clonedProduct);
            newCards.push(clonedProduct);
        });
        
        console.log(`Appended ${newCards.length} products to grid`);
        console.log('Total products in grid after append:', this.productGrid.querySelectorAll('.product-card').length);
        
        // Process the new products for location filtering
        if (window.productCardDataManager) {
            console.log(`Processing ${newCards.length} newly loaded products...`);
            window.productCardDataManager.processAllProductCards();
        }
        
        // Update pagination
        const nextPagination = doc.querySelector('.pagination-wrapper');
        if (nextPagination) {
            this.pagination.innerHTML = nextPagination.innerHTML;
            this.normalizePaginationLinks();
        } else {
            this.hasMorePages = false;
        }
        
        // Check pagination status again
        this.checkPagination();
        
        this.hideLoadingIndicator();
        this.isLoading = false;
        
        // NOTE: Filter count recalculation is DISABLED for native S&D filters
        // Recalculate filter counts after loading new products
        // setTimeout(() => {
        //     this.recalculateFilterCounts();
        // }, 500);
        
        // Check again if we need more products (in case many were filtered out)
        setTimeout(() => {
            this.checkIfNeedsMoreProducts();
        }, 1500);
        
        } catch (error) {
        console.error('Error loading next page:', error);
        this.hideLoadingIndicator();
        this.isLoading = false;
        }
    }
    
  showLoadingIndicator() {
    if (this.loadingIndicator) {
      this.loadingIndicator.style.display = 'flex';
    }
  }
  
  hideLoadingIndicator() {
    if (this.loadingIndicator) {
      this.loadingIndicator.style.display = 'none';
    }
  }

    getCurrentLocation() {
        return window.locationManager?.currentLocation ||
            localStorage.getItem('selectedLocation') ||
            localStorage.getItem('currentPickingStore') ||
            '76';
    }

    withLocationFilter(url) {
        const nextUrl = new URL(url, window.location.origin);
        nextUrl.searchParams.set('filter.p.m.custom.available_locations', this.getCurrentLocation());
        return nextUrl.toString();
    }

    normalizePaginationLinks() {
        this.pagination.querySelectorAll('a[href]').forEach(link => {
            link.href = this.withLocationFilter(link.href);
        });
    }
    
    decodeHTMLEntities(text) {
        const textarea = document.createElement('textarea');
        textarea.innerHTML = text;
        return textarea.value;
    }
    
    recalculateFilterCounts() {
        console.log('Recalculating filter counts for search results...');
        
        // Get all visible product cards
        const visibleProducts = Array.from(
            this.productGrid.querySelectorAll('.product-card:not(.product-loading)')
        );
        
        if (visibleProducts.length === 0) {
            console.log('No visible products to count');
            return;
        }
        
        console.log(`Counting filters for ${visibleProducts.length} visible products`);
        
        // Count filters by analyzing visible products
        const filterCounts = new Map();
        
        visibleProducts.forEach(card => {
            // Category (custom.category metafield)
            const category = card.getAttribute('data-category');
            if (category) {
                const categories = this.parseMetafieldValue(category);
                categories.forEach(cat => {
                    const key = `category_${cat}`;
                    filterCounts.set(key, (filterCounts.get(key) || 0) + 1);
                });
            }
            
            // Dietary & Lifestyle (custom.dietary_lifestyle metafield)
            const dietary = card.getAttribute('data-dietary');
            if (dietary) {
                const dietaryItems = this.parseMetafieldValue(dietary);
                dietaryItems.forEach(item => {
                    const key = `dietary_lifestyle_${item}`;
                    filterCounts.set(key, (filterCounts.get(key) || 0) + 1);
                });
            }
            
            // Allergens (custom.allergens metafield)
            const allergens = card.getAttribute('data-allergens');
            if (allergens) {
                const allergenItems = this.parseMetafieldValue(allergens);
                allergenItems.forEach(item => {
                    const key = `allergens_${item}`;
                    filterCounts.set(key, (filterCounts.get(key) || 0) + 1);
                });
            }
            
            // Product type
            const productType = card.getAttribute('data-product-type');
            if (productType) {
                const key = `product_type_${productType}`;
                filterCounts.set(key, (filterCounts.get(key) || 0) + 1);
            }
            
            // Vendor
            const vendor = card.getAttribute('data-vendor');
            if (vendor) {
                const key = `vendor_${vendor}`;
                filterCounts.set(key, (filterCounts.get(key) || 0) + 1);
            }
        });
        
        console.log('Filter counts calculated:', filterCounts.size, 'unique filters');
        
        // Update the filter UI
        this.updateFilterCountsInUI(filterCounts);
    }
    
    parseMetafieldValue(value) {
        if (!value || value === '-' || value === 'null') return [];
        
        // Try to parse as JSON array first
        if (value.startsWith('[') && value.endsWith(']')) {
            try {
                const parsed = JSON.parse(value);
                return Array.isArray(parsed) ? parsed : [];
            } catch (e) {
                console.warn('Failed to parse JSON array:', value);
            }
        }
        
        // Otherwise split by comma
        return value.split(',').map(v => v.trim()).filter(v => v);
    }
    
    updateFilterCountsInUI(filterCounts) {
        const allCheckboxes = document.querySelectorAll('.facets input[type="checkbox"]');
        console.log(`Updating ${allCheckboxes.length} filter checkboxes`);
        
        let updatedCount = 0;
        
        allCheckboxes.forEach((checkbox) => {
            const checkboxValue = checkbox.value;
            let filterType = null;
            
            // Detect filter type from the checkbox's name or parent structure
            const name = checkbox.name;
            if (name.includes('filter.p.m.custom.category')) {
                filterType = 'category';
            } else if (name.includes('filter.p.m.custom.dietary_lifestyle')) {
                filterType = 'dietary_lifestyle';
            } else if (name.includes('filter.p.m.custom.allergens')) {
                filterType = 'allergens';
            } else if (name.includes('filter.p.product_type')) {
                filterType = 'product_type';
            } else if (name.includes('filter.p.vendor')) {
                filterType = 'vendor';
            }
            
            // Look up count using detected filter type
            if (filterType) {
                const key = `${filterType}_${checkboxValue}`;
                const matchingCount = filterCounts.get(key) || 0;
                
                // Find the label and update count
                const label = checkbox.closest('label') || checkbox.parentElement;
                if (label) {
                    // Find the count display span
                    const countSpan = label.querySelector('span[aria-hidden="true"]');
                    
                    if (countSpan) {
                        // Update the count in parentheses
                        countSpan.textContent = countSpan.textContent.replace(/\(\d+\)/, `(${matchingCount})`);
                        updatedCount++;
                        
                        // Disable if no products available and not currently selected
                        if (matchingCount === 0 && !checkbox.checked) {
                            checkbox.disabled = true;
                            label.classList.add('facet-checkbox--disabled');
                        } else {
                            checkbox.disabled = false;
                            label.classList.remove('facet-checkbox--disabled');
                        }
                    }
                }
            }
        });
        
        console.log(`Updated ${updatedCount} filter counts in search`);
        
        // Update the visible product count (accurate to the current picking store:
        // exclude cards hidden because the product is unavailable at this location).
        const visibleProducts = Array.from(
            this.productGrid.querySelectorAll('.product-card:not(.product-loading):not(.location-unavailable)')
        );
        
        const countElements = document.querySelectorAll('#ProductCount, #ProductCountDesktop');
        countElements.forEach(element => {
            if (element) {
                const searchTerm = new URLSearchParams(window.location.search).get('q') || '';
                const countText = visibleProducts.length === 1 ? 
                    `1 result for "${searchTerm}"` : 
                    `${visibleProducts.length} results for "${searchTerm}"`;
                element.innerHTML = `<span class="product-count-text">${countText}</span>`;
            }
        });
    }
    }

    // Initialize when DOM is ready and ProductCardDataManager is available
    document.addEventListener('DOMContentLoaded', () => {
    const checkAndInit = () => {
        if (window.productCardDataManager) {
        console.log('Initializing search infinite scroll');
        new SearchInfiniteScroll();
        } else {
        console.log('Waiting for ProductCardDataManager...');
        setTimeout(checkAndInit, 100);
        }
    };
    checkAndInit();
    });

